import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { FiShield, FiLock, FiMail, FiArrowLeft } from 'react-icons/fi';
import toast from 'react-hot-toast';
import SEO from '../components/common/SEO';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { adminLoginUser, clearError } from '../store/slices/authSlice';

export default function AdminLoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((s) => s.auth);
  const [form, setForm] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    const result = await dispatch(adminLoginUser(form));
    if (adminLoginUser.fulfilled.match(result)) {
      toast.success('Welcome, Admin!');
      navigate('/admin');
    } else {
      toast.error(result.payload || 'Admin login failed');
    }
  };

  return (
    <>
      <SEO title="Admin Portal Login" />
      <div className="min-h-screen flex">
        {/* Left panel — branding */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 via-brand-900 to-gray-950 text-white p-12 flex-col justify-between relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-30">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-32 h-32 rounded-full bg-brand-500 blur-3xl"
                style={{ left: `${10 + i * 15}%`, top: `${20 + (i % 3) * 25}%` }}
                animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 4 + i, repeat: Infinity }}
              />
            ))}
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-brand-500/20 border border-brand-400/30 flex items-center justify-center">
                <FiShield className="w-6 h-6 text-brand-300" />
              </div>
              <div>
                <p className="text-brand-300 text-sm font-medium">Secure Admin Portal</p>
                <h1 className="text-2xl font-display font-bold">Paise Ki Pathshala</h1>
              </div>
            </div>
            <h2 className="text-4xl font-display font-extrabold leading-tight mb-4">
              Manage Content.<br />Empower Learners.
            </h2>
            <p className="text-gray-400 text-lg max-w-md">
              Control lessons, tips, stories, page content, landing animations, users, and platform settings from one powerful dashboard.
            </p>
          </div>
          <div className="relative z-10 grid grid-cols-2 gap-4">
            {['📊 Analytics', '📚 Lessons', '🎨 Animations', '👥 Users'].map((item) => (
              <div key={item} className="bg-white/5 border border-white/10 rounded-xl p-4 text-sm backdrop-blur-sm">
                {item}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right panel — login form */}
        <div className="flex-1 flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-950">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand-600 mb-8">
              <FiArrowLeft /> Back to website
            </Link>

            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center mb-4 shadow-lg shadow-brand-500/30">
                  <FiLock className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-display font-bold text-gray-900 dark:text-white">Admin Login</h2>
                <p className="text-gray-500 text-sm mt-1">Authorized personnel only</p>
              </div>

              {loading ? (
                <LoadingSpinner className="py-8" />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Admin Email</label>
                    <div className="relative">
                      <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        className="input-field pl-10"
                        placeholder="admin@paisekipathshala.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Password</label>
                    <div className="relative">
                      <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="password"
                        className="input-field pl-10"
                        placeholder="••••••••"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  {error && <p className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{error}</p>}
                  <button type="submit" className="w-full py-3 bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold rounded-xl hover:from-brand-700 hover:to-brand-800 transition-all shadow-lg">
                    Sign In to Admin Panel
                  </button>
                </form>
              )}

              <p className="text-center text-xs text-gray-400 mt-6">
                Regular user?{' '}
                <Link to="/login" className="text-brand-600 hover:underline">User Login</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
